/**
 * Order Vuex module for Portal Application
 * Controls the data and actions for orders
 * 
 * @module Orders
 * @see
 * @author Carmen Totino(Fresche Solutions)
 * @date August 2023
 * Based on Invoices
 */

import portalapi from "@services/portalapi";
import { resetStateData } from "@/utils/vuexStoreUtils";
import { getField, updateField } from "vuex-map-fields";

const DEFAULT_ORDER = {
    orderFound: null,
    headerDetails: {},
    lines: [],
};

const DEFAULT_ORDER_CONFIRMATION = {
    confirmationFound: null,
};

const DEFAULT_STATE = {
    order: DEFAULT_ORDER,
    orderList: [],
    orderTypeOptions: [],
    orderStatusOptions: [],
    orderConfirmation: DEFAULT_ORDER_CONFIRMATION,
};

const state = structuredClone(DEFAULT_STATE);

const getters = {
    getField, // for vuex-map-fields

    getOrder(st) {
        return st.order;
    },

    getOrderList(st) {
        return st.orderList;
    },

    getOrderTypeOptions(st) {
        return st.orderTypeOptions;
    },

    getOrderStatusOptions(st) {
        return st.orderStatusOptions;
    },

    getOrderConfirmation(st) {
        return st.orderConfirmation;
    },
};

const mutations = {
    updateField, // for vuex-map-fields

    async clearStateData(st) {
        st = resetStateData(st, DEFAULT_STATE);
    },
    
    setOrder(st, payload) {
        st.order = payload;
    },
    
    setOrderList(st, payload) {
        st.orderList = payload;
    },

    setOrderTypeOptions(st, payload) {
        st.orderTypeOptions = payload;
    },

    setOrderStatusOptions(st, payload) {
        st.orderStatusOptions = payload;
    },

    setOrderConfirmation(st, payload) {
        st.orderConfirmation = payload;
    },
};

const actions = {
    // Get all Orders
    async fetchAllOrders({ commit }, payload) 
    {
        const requestPayload = {
            page: (payload && payload.page ? payload.page : 1),
            itemsPerPage: (payload && payload.itemsPerPage ? payload.itemsPerPage : 20),
            filters: (payload && payload.filters ? payload.filters : []),
            sortBy: (payload && payload.sortBy ? payload.sortBy : {column: 'uwdOrderNum', state: 'desc'}),
        };

        const response = await portalapi.moduleOrders.getAllOrders(requestPayload);

        if (response && response.status === vm.config.globalProperties.$constants.HTTP_STATUS_CODES.OK) {
            commit("setOrderList", response.data);
        } 
    },


    // Get and save Order into VueX store
    async fetchOrder({ commit }, orderNum) {
        commit("setOrder", DEFAULT_ORDER);   

        let order = await actions.retrieveOrder({ commit }, orderNum);

        commit("setOrder", order);
    },
    

    // Get order types
    async fetchOrderTypeOptions(context) 
    {
        let commit = context.commit;

        if (context.state.orderTypeOptions.length == 0) {
            const response = await portalapi.moduleOrders.getSalesOrderTypes();

            if (response && response.status === vm.config.globalProperties.$constants.HTTP_STATUS_CODES.OK) {
                commit("setOrderTypeOptions", response.data);
            } 
        }
    },


    // Get order statuses
    async fetchOrderStatusOptions(context) 
    {
        let commit = context.commit;

        if (context.state.orderStatusOptions.length == 0) {
            const response = await portalapi.moduleOrders.getSalesOrderStatus();

            if (response.status === vm.config.globalProperties.$constants.HTTP_STATUS_CODES.OK) {
                commit("setOrderStatusOptions", response.data);
            }
        }
    },
    

    // Get and return Order
    async retrieveOrder({ commit }, orderNum) {
        let response = await portalapi.moduleOrders.getOrder(orderNum);
    
        // Check if the order was found
        if (response && response.status === vm.config.globalProperties.$constants.HTTP_STATUS_CODES.OK) {
            response = response.data;
            response.orderFound = true;
        } 
        else {
            response.orderFound = false;
        }

        return response;
    },

    
    // Get and save Order Confirmation into VueX store
    async fetchOrderConfirmation({ commit }, orderNum)
    {
        commit("setOrderConfirmation", DEFAULT_ORDER_CONFIRMATION);   

        let response = await portalapi.moduleOrders.getOrderConfirmation(orderNum);

        // Check if the order confirmation was found
        if (
            response && response.status === vm.config.globalProperties.$constants.HTTP_STATUS_CODES.OK 
            && response.data && response.data.status === vm.config.globalProperties.$constants.STATUS_SUCCESS
        ) {
            response = response.data.orderConfirmation;
            response.confirmationFound = true;
        }
        else {
            response = {
                confirmationFound: false,
            };
        }

        commit("setOrderConfirmation", response);
    },
};

export default {
    namespaced: true,
    state,
    getters,
    mutations,
    actions,
};